import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

import {
  getQuotations,
  getProformaInvoices,
  getSOAs
} from "../services/firestore";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "../components/ui/card";

import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

import {
  FileText,
  Receipt,
  ClipboardList,
  TrendingUp,
  Plus
} from "lucide-react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";

import { toast } from "sonner";

export const DashboardPage = () => {

const [statsFilter, setStatsFilter] = useState("all");
const [customDate, setCustomDate] = useState("");

  const { userData } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("monthly");

const [stats, setStats] = useState({
quotations: {
  total: 0,
  draft: 0,
  submitted: 0,
  in_process: 0,
  won: 0,
  lost: 0
},
  proforma: {
    total: 0,
    draft: 0,
    submitted: 0,
    in_process: 0,
    payment_received: 0,
    lost: 0
  },
  soa: {
    total: 0,
    draft: 0,
    submitted: 0,
    in_process: 0,
    completed: 0
  }
});

  const [trendData, setTrendData] = useState([]);
  const [recentDocs, setRecentDocs] = useState([]);

useEffect(() => {
  fetchDashboardData();
}, [userData, timeFilter, statsFilter, customDate]);

  /* =============================
     FILTER LOGIC
  ==============================*/

const applyStatsFilter = (docs) => {

  if (statsFilter === "all") return docs;

  const now = new Date();

  return docs.filter(doc => {

    const dRaw = doc.created_at?.toDate?.() || doc.created_at;
if (!dRaw) return false;

const d = new Date(dRaw);
if (isNaN(d)) return false;

    if (statsFilter === "last7") {
      const past = new Date();
      past.setDate(now.getDate() - 7);
      return d >= past;
    }

    if (statsFilter === "last30") {
      const past = new Date();
      past.setDate(now.getDate() - 30);
      return d >= past;
    }

    if (statsFilter === "custom") {
      if (!customDate) return true;

      const selected = new Date(customDate);
      selected.setHours(0, 0, 0, 0);

      const dDate = new Date(d);
      dDate.setHours(0, 0, 0, 0);

      return dDate >= selected;
    }

    return true;

  });

};



  const applyTimeFilter = (docs) => {

    if (timeFilter === "all") return docs;

    const now = new Date();

    return docs.filter(doc => {
      const dRaw = doc.created_at?.toDate?.() || doc.created_at;
if (!dRaw) return false;

const d = new Date(dRaw);
if (isNaN(d)) return false;

      if (timeFilter === "weekly") {
        const weekAgo = new Date();
        weekAgo.setDate(now.getDate() - 7);
        return d >= weekAgo;
      }

      if (timeFilter === "monthly") {
        const monthAgo = new Date();
        monthAgo.setMonth(now.getMonth() - 1);
        return d >= monthAgo;
      }

      return true;
    });
  };

  /* =============================
     FETCH DATA
  ==============================*/

  const fetchDashboardData = async () => {

    if (!userData) return;

    setLoading(true);

    try {

      const [qRaw, pRaw, sRaw] = await Promise.all([
        getQuotations(),
        getProformaInvoices(),
        getSOAs()
      ]);

//  FOR STATS
const quotations = applyStatsFilter(qRaw);
const pis = applyStatsFilter(pRaw);
const soas = applyStatsFilter(sRaw);

//  FOR GRAPH (SEPARATE DATA)
const qTrend = applyTimeFilter(qRaw);
const pTrend = applyTimeFilter(pRaw);
const sTrend = applyTimeFilter(sRaw);

      /* ===== STATUS COUNTS ===== */

      const countStatus = (arr, status) =>
        arr.filter(x => (x.status || "draft") === status).length;

      setStats({

        quotations: {
          total: quotations.length,
          draft: countStatus(quotations, "draft"),
          submitted: countStatus(quotations, "submitted"),
          in_process: countStatus(quotations, "in_process"),
          won: countStatus(quotations, "won"),
          lost: countStatus(quotations, "lost")
        },

        proforma: {
          total: pis.length,
          draft: countStatus(pis, "draft"),
          submitted: countStatus(pis, "submitted"),
          in_process: countStatus(pis, "in_process"),
          payment_received: countStatus(pis, "payment_received"),
          lost: countStatus(pis, "lost")
        },

        soa: {
          total: soas.length,
          draft: countStatus(soas, "draft"),
          submitted: countStatus(soas, "submitted"),
          in_process: countStatus(soas, "in_process"),
          completed: countStatus(soas, "completed")
        }

      });

      /* ===== TREND DATA ===== */

      const buildTrend = (docs) => {

        const map = {};

       docs.forEach(doc => {

  const dRaw = doc.created_at?.toDate?.() || doc.created_at;
  if (!dRaw) return;

  const d = new Date(dRaw);
  if (isNaN(d)) return;


          let key;

          if (timeFilter === "weekly") {
            key = d.toLocaleDateString("en-IN", { weekday: "short" });
          } else {
            key = d.toLocaleDateString("en-IN", { month: "short" });
          }

          map[key] = (map[key] || 0) + 1;

        });

        return Object.entries(map).map(([name, value]) => ({
          name,
          value
        }));

      };

      setTrendData(buildTrend([...qTrend, ...pTrend, ...sTrend]));

      /* ===== RECENT ===== */

      const docs = [
  ...qRaw.map(q => ({ ...q, type: "Quotation", docNo: q.quotation_no })),
  ...pRaw.map(p => ({ ...p, type: "PI", docNo: p.pi_no })),
  ...sRaw.map(s => ({ ...s, type: "SOA", docNo: s.soa_no }))
]
.sort((a, b) => {
  const getDate = (doc) => {
    const dRaw = doc.created_at?.toDate?.() || doc.created_at;
    const d = new Date(dRaw);
    return isNaN(d) ? new Date(0) : d;
  };

  return getDate(b) - getDate(a);
});

setRecentDocs(docs.slice(0, 6));   //  ADD THIS

} catch (error) {

  console.error(error);
  toast.error("Dashboard load failed");

} finally {

  setLoading(false);

}

};


  /* =============================
     LOADING
  ==============================*/

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-40" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  /* =============================
     PAGE
  ==============================*/

  return (

    <div className="p-6 space-y-6">

      {/* HEADER */}

      <div>
        <h1 className="text-2xl font-bold">
          Welcome {userData?.displayName || "User"}
        </h1>
        <p className="text-slate-500">Sales Dashboard</p>
      </div>


{/* ===== STATS FILTER ===== */}

<div className="flex gap-3 flex-wrap items-center">

<span className="text-sm text-slate-500">Filter:</span>

  <select
    value={statsFilter}
    onChange={(e) => setStatsFilter(e.target.value)}
    className="border rounded px-3 py-2"
  >
    <option value="all">All</option>
    <option value="last7">Last 7 Days</option>
    <option value="last30">Last 30 Days</option>
    <option value="custom">Custom Date</option>
  </select>

  {statsFilter === "custom" && (
  <>
    <input
      type="date"
      value={customDate}
      onChange={(e) => setCustomDate(e.target.value)}
      className="border rounded px-3 py-2"
    />

    {!customDate && (
      <p className="text-xs text-red-500">
        Please select a date
      </p>
    )}
  </>
)}

</div>

      {/* ===== TOP CARDS ===== */}

      <div className="grid md:grid-cols-3 gap-4">

        {/* QUOTATIONS */}

        <Card onClick={() => navigate("/quotations")} className="cursor-pointer">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm text-slate-500">Quotations</p>
            <p className="text-3xl font-bold">{stats.quotations.total}</p>
            <p className="text-xs">
              Draft {stats.quotations.draft} • Submitted {stats.quotations.submitted} • In Process {stats.quotations.in_process} • Won {stats.quotations.won} • Lost {stats.quotations.lost}
            </p>
          </CardContent>
        </Card>

        {/* PI */}

        <Card onClick={() => navigate("/proforma-invoices")} className="cursor-pointer">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm text-slate-500">Proforma</p>
            <p className="text-3xl font-bold">{stats.proforma.total}</p>
            <p className="text-xs">
              Draft {stats.proforma.draft} • Submitted {stats.proforma.submitted} • In Process {stats.proforma.in_process} • Paid {stats.proforma.payment_received} • Lost {stats.proforma.lost}
            </p>
          </CardContent>
        </Card>

        {/* SOA */}

        <Card onClick={() => navigate("/soa")} className="cursor-pointer">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm text-slate-500">SOA</p>
            <p className="text-3xl font-bold">{stats.soa.total}</p>
            <p className="text-xs">
              Draft {stats.soa.draft} • Submitted {stats.soa.submitted} • In Process {stats.soa.in_process} • Completed {stats.soa.completed}
            </p>
          </CardContent>
        </Card>

      </div>

      {/* ===== GRAPH ===== */}

      <Card>

        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle className="flex gap-2">
            <TrendingUp size={18} /> Trends
          </CardTitle>

          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="border rounded p-1 text-sm"
          >
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="all">All Time</option>
          </select>

        </CardHeader>

        <CardContent>

          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={trendData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>

        </CardContent>

      </Card>

      {/* ===== QUICK ACTIONS ===== */}

      <Card>

        <CardHeader>
          <CardTitle className="flex gap-2">
            <Plus size={18} /> Quick Actions
          </CardTitle>
        </CardHeader>

        <CardContent className="flex gap-3 flex-wrap">

          <Button onClick={() => navigate("/quotations/new")}>
            <FileText size={16} className="mr-2" />
            New Quotation
          </Button>

          <Button onClick={() => navigate("/proforma-invoices/new")}>
            <Receipt size={16} className="mr-2" />
            New PI
          </Button>

          <Button onClick={() => navigate("/soa/new")}>
            <ClipboardList size={16} className="mr-2" />
            New SOA
          </Button>

<Button
  variant="outline"
  onClick={() => {
    setStatsFilter("all");
    setCustomDate("");
  }}
>
  Clear
</Button>

        </CardContent>

      </Card>

      {/* ===== RECENT ===== */}

      <Card>

        <CardHeader>
          <CardTitle>Recent Documents</CardTitle>
        </CardHeader>

        <CardContent className="space-y-2">

          {recentDocs.map(doc => {

            const path =
              doc.type === "Quotation"
                ? "/quotations"
                : doc.type === "PI"
                ? "/proforma-invoices"
                : "/soa";

            return (

              <div
                key={doc.id}
                onClick={() => navigate(`${path}/${doc.id}`)}
                className="p-3 bg-slate-50 rounded cursor-pointer hover:bg-slate-100"
              >
                <p className="font-semibold">{doc.docNo}</p>
                <p className="text-sm text-slate-500">
                  {doc.party_snapshot?.name || "No party"}
                </p>
                <span className="text-xs">{doc.status}</span>
              </div>

            );

          })}

        </CardContent>

      </Card>

    </div>
  );
};

export default DashboardPage;